import { useMemo } from 'react';
import { useCountry } from './useCountry';
import { convertCurrency } from '@/utils/countryConfig';
import { useFxRates } from '@/lib/fxRates';
import type { Currency } from '@/types/listings-v2';

export const useCurrencyConversion = () => {
  const { config } = useCountry();
  const { data: fxRates } = useFxRates();

  const formatPrice = useMemo(() => {
    return (price: number, originalCurrency?: Currency | string | null, options?: { showOriginal?: boolean }) => {
      // Normalizează currency - acceptă și string pentru compatibilitate cu DB
      const normalizedCurrency: Currency = 
        (originalCurrency && ['EUR', 'GBP', 'USD', 'RON'].includes(originalCurrency.toUpperCase())) 
          ? originalCurrency.toUpperCase() as Currency 
          : 'EUR';
      
      // Moneda implicită a site-ului
      let displayCurrency: Currency = config.currency as Currency;

      // Pentru prețurile deja în RON, păstrăm întotdeauna RON (fără conversie)
      if (normalizedCurrency === 'RON') {
        displayCurrency = 'RON';
      }

      let needsConversion = displayCurrency !== normalizedCurrency;
      let baseAmount = price;
      if (needsConversion) {
        const converted = convertCurrency(price, normalizedCurrency as any, displayCurrency as any, fxRates);
        // No rate loaded → show the original amount instead of a guessed conversion.
        if (converted === null) { displayCurrency = normalizedCurrency; needsConversion = false; }
        else baseAmount = converted;
      }
      const truncatedAmount = Math.floor(baseAmount);

      // Formatare cu locale
      const formatted = new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: displayCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(truncatedAmount);

      // Afișează și moneda originală dacă s-a făcut conversie
      if (options?.showOriginal && needsConversion) {
        const originalFormatted = new Intl.NumberFormat(config.locale, {
          style: 'currency',
          currency: normalizedCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Math.floor(price));
        return `${formatted} (≈ ${originalFormatted})`;
      }

      return formatted;
    };
  }, [config.currency, config.locale, fxRates]);

  const formatPriceWithVAT = useMemo(() => {
    return (price: number, originalCurrency: Currency = 'EUR', includedVAT: boolean = true) => {
      const baseFormatted = formatPrice(price, originalCurrency);
      
      if (!config.showVAT) {
        return `${baseFormatted} ${includedVAT ? '(incl. VAT)' : '(excl. VAT)'}`;
      }
      
      if (!includedVAT) {
        const priceWithVAT = price * (1 + config.vatRate / 100);
        const formattedWithVAT = formatPrice(priceWithVAT, originalCurrency);
        return `${baseFormatted} (excl. VAT) / ${formattedWithVAT} (incl. VAT ${config.vatRate}%)`;
      }
      
      return `${baseFormatted} (incl. VAT ${config.vatRate}%)`;
    };
  }, [formatPrice, config.showVAT, config.vatRate]);

  return {
    formatPrice,
    formatPriceWithVAT,
    currency: config.currency,
    locale: config.locale,
  };
};
